// #Usage
//const { showToast } = useToasts()
//showToast("Saved successfully!", "success")

export type ToastType = "success" | "error" | "info";

export interface ToastItem {
  id: number;
  message: string;
  type: ToastType;

  // timing/progress
  duration: number; // total duration (ms)
  remaining: number; // remaining time (ms)
  startedAt: number; // last resume timestamp (ms)
  timer?: ReturnType<typeof setTimeout> | null;

  // pause + progress bar animation control
  isPaused: boolean;
  progress: number; // 0..100
}

const toasts = ref<ToastItem[]>([]);
let counter = 0;

export function useToasts() {
  const defaultTimeout = 3000;
  let progressInterval: ReturnType<typeof setInterval> | null = null;
  const startTimer = (toast: ToastItem) => {
    toast.startedAt = Date.now();
    toast.isPaused = false;

    // clear old timer if any
    if (toast.timer) clearTimeout(toast.timer);

    toast.timer = setTimeout(() => {
      removeToast(toast.id);
    }, toast.remaining);
  };

  const pauseToast = (id: number) => {
    const toast = toasts.value.find((t) => t.id === id);
    if (!toast || toast.isPaused) return;

    toast.isPaused = true;

    // compute elapsed since last resume
    const elapsed = Date.now() - toast.startedAt;
    toast.remaining = Math.max(0, toast.remaining - elapsed);

    if (toast.timer) {
      clearTimeout(toast.timer);
      toast.timer = null;
    }
  };

  const resumeToast = (id: number) => {
    const toast = toasts.value.find((t) => t.id === id);
    if (!toast || !toast.isPaused) return;

    startTimer(toast);
  };
  const ensureProgressEngine = () => {
    if (progressInterval) return;
    progressInterval = setInterval(() => {
      tickProgress();
      if (toasts.value.length === 0 && progressInterval) {
        clearInterval(progressInterval);
        progressInterval = null;
      }
    }, 100); // 20fps is plenty for a progress bar
  };
  const showToast = (
    message: string,
    type: ToastType = "info",
    duration = defaultTimeout
  ) => {
    const id = ++counter;

    const toast: ToastItem = {
      id,
      message,
      type,
      duration,
      remaining: duration,
      startedAt: Date.now(),
      timer: null,
      isPaused: false,
      progress: 100,
    };

    toasts.value.push(toast);
    startTimer(toast);
    ensureProgressEngine();
  };

  const removeToast = (id: number) => {
    const toast = toasts.value.find((t) => t.id === id);
    if (toast?.timer) clearTimeout(toast.timer);
    toasts.value = toasts.value.filter((t) => t.id !== id);
  };

  // for progress bar (called by UI on an interval)

  const tickProgress = () => {
    const now = Date.now();

    for (const t of toasts.value) {
      const liveRemaining = t.isPaused
        ? t.remaining
        : Math.max(0, t.remaining - (now - t.startedAt));

      t.progress = (liveRemaining / t.duration) * 100; // 100 -> 0
    }
  };

  return {
    toasts,
    showToast,
    removeToast,
    pauseToast,
    resumeToast,
    tickProgress,
    defaultTimeout,
  };
}
